import { Link } from 'react-router-dom';
import { DocsLayout } from '../components/DocsLayout';

export function ScriptStepPage() {
  return (
    <DocsLayout
      title="Custom Script step"
      updated="May 9, 2026"
      summary="Drop arbitrary TypeScript, Python, or Java into a flow when the built-in steps don't quite fit. Outputs become flow variables, optionally persisted to the project."
    >
      <h2>When to reach for a script</h2>
      <p>The Custom Script step is the escape hatch. Reach for it when you need to:</p>
      <ul>
        <li>Generate test data (random emails, dates, JWTs, signed URLs).</li>
        <li>Compute values from previous-step outputs (parse JSON, slice arrays, hash payloads).</li>
        <li>Call a small piece of business logic that lives in TypeScript already.</li>
        <li>Do a quick transform that would otherwise need three or four built-in steps chained together.</li>
      </ul>
      <p>
        Don't use it as a general-purpose runtime — for long-running computation, prefer your own service and a Call
        Endpoint step, since the Script step is sandboxed with strict CPU and memory limits.
      </p>

      <h2>Languages</h2>
      <table>
        <thead>
          <tr>
            <th>Language</th>
            <th>Engine</th>
            <th>Default timeout</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>TypeScript</td>
            <td>Node.js 20 (in-process VM)</td>
            <td>5s</td>
          </tr>
          <tr>
            <td>Python</td>
            <td>Python 3.12 (sandboxed subprocess)</td>
            <td>10s</td>
          </tr>
          <tr>
            <td>Java</td>
            <td>Eclipse Temurin 21 (sandboxed subprocess)</td>
            <td>15s</td>
          </tr>
        </tbody>
      </table>

      <h2>The runtime API</h2>
      <p>
        Every script receives a <code>rynora</code> object (or module, in Java) that exposes the same minimal surface in
        all three languages:
      </p>
      <ul>
        <li>
          <code>rynora.input(name)</code> — read a flow variable. Returns string, number, boolean, array, or object.
        </li>
        <li>
          <code>rynora.output(name, value)</code> — emit a flow variable. The next step can reference it by name.
        </li>
        <li>
          <code>rynora.log(message)</code> — append a structured log line that shows up in the run report.
        </li>
        <li>
          <code>rynora.fail(message)</code> — fail the step with a custom message. The flow stops unless the step is
          marked Continue-on-error.
        </li>
        <li>
          <code>rynora.vault.read(key)</code> — read an encrypted project secret by name. Audit-logged.
        </li>
      </ul>

      <h3>TypeScript example</h3>
      <pre className="docs-codeblock">{`const orderId = rynora.input('orderId') as string;
const ts = Date.now();
const signature = await crypto.subtle.digest(
  'SHA-256',
  new TextEncoder().encode(orderId + ':' + ts),
);

rynora.output('signature', Buffer.from(signature).toString('hex'));
rynora.output('timestamp', ts);`}</pre>

      <h3>Python example</h3>
      <pre className="docs-codeblock">{`import hashlib, time

order_id = rynora.input('orderId')
ts = int(time.time() * 1000)

signature = hashlib.sha256(f"{order_id}:{ts}".encode()).hexdigest()

rynora.output('signature', signature)
rynora.output('timestamp', ts)`}</pre>

      <h3>Java example</h3>
      <pre className="docs-codeblock">{`import java.security.MessageDigest;

String orderId = (String) rynora.input("orderId");
long ts = System.currentTimeMillis();

byte[] hash = MessageDigest.getInstance("SHA-256")
    .digest((orderId + ":" + ts).getBytes("UTF-8"));

StringBuilder hex = new StringBuilder();
for (byte b : hash) hex.append(String.format("%02x", b));

rynora.output("signature", hex.toString());
rynora.output("timestamp", ts);`}</pre>

      <h2>Output variables</h2>
      <p>
        Each declared output has a <strong>name</strong>, a <strong>type</strong>, and a <strong>scope</strong>:
      </p>
      <ul>
        <li>
          <strong>Flow scope</strong> (default) — the value is available to every later step in the same run, then
          discarded.
        </li>
        <li>
          <strong>Project scope</strong> — the value is written to the project's variables on the server (
          <code>POST /api/projects/:id/variables</code>) and is visible to every flow in the same project until you
          delete or overwrite it. Mark the variable as <em>secret</em> if it contains a credential — secrets are
          encrypted at rest and only decrypted when explicitly revealed.
        </li>
      </ul>

      <h3>Allowed types</h3>
      <p>
        Each output is declared with a type so we can validate it at the end of the script: <code>string</code>,{' '}
        <code>number</code>, <code>boolean</code>, <code>array</code>, <code>object</code>, or <code>date</code>{' '}
        (ISO-8601). If the script returns a value that doesn't match its declared type the step fails with{' '}
        <code>SCRIPT_OUTPUT_TYPE_MISMATCH</code>.
      </p>

      <h2>Limits and quotas</h2>
      <ul>
        <li>Code body capped at 64 KB.</li>
        <li>CPU per run capped per language (see Languages table above).</li>
        <li>Memory capped at 256 MB.</li>
        <li>Network access disabled by default — call out via the Call Endpoint step instead.</li>
        <li>File system access limited to a per-step temp directory wiped at the end of the run.</li>
        <li>
          Plan-tier quotas apply on top — see <Link to="/docs/plans">Plans &amp; limits</Link>.
        </li>
      </ul>

      <h2>Errors you might see</h2>
      <table>
        <thead>
          <tr>
            <th>Code</th>
            <th>Meaning</th>
            <th>What to do</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>SCRIPT_TIMEOUT</code>
            </td>
            <td>The script exceeded its language's default timeout.</td>
            <td>
              Reduce work or split into smaller scripts; raise the per-step timeout from the editor (Pro plan and
              above).
            </td>
          </tr>
          <tr>
            <td>
              <code>SCRIPT_RUNTIME_ERROR</code>
            </td>
            <td>The script threw or exited non-zero.</td>
            <td>Look at the run report — Rynora captures stderr and exit code. Wrap risky calls in try/catch.</td>
          </tr>
          <tr>
            <td>
              <code>SCRIPT_OUTPUT_MISSING</code>
            </td>
            <td>An output declared in the editor was never written.</td>
            <td>
              Either remove the output declaration or call <code>rynora.output(name, …)</code> before the script ends.
            </td>
          </tr>
          <tr>
            <td>
              <code>SCRIPT_OUTPUT_TYPE_MISMATCH</code>
            </td>
            <td>An output's runtime value didn't match its declared type.</td>
            <td>
              Coerce the value (<code>String(value)</code>, <code>Number(value)</code>) before emitting it.
            </td>
          </tr>
          <tr>
            <td>
              <code>SCRIPT_RUNTIME_UNAVAILABLE</code>
            </td>
            <td>The selected language's runtime image isn't installed on the runner.</td>
            <td>
              Operator: rebuild the runner image with the language pack. See{' '}
              <Link to="/docs/self-hosting">Self-hosting</Link>.
            </td>
          </tr>
        </tbody>
      </table>

      <h2>Debugging tips</h2>
      <ul>
        <li>
          Use <code>rynora.log()</code> liberally — log lines show up next to step output in the run report.
        </li>
        <li>
          The editor's &quot;Test step&quot; button runs your script with the current preview values, so you don't need
          a full flow run to iterate.
        </li>
        <li>
          Every script execution is audit-logged with a hash of the code (not the source). Identical scripts run within
          60 seconds collapse into one row to keep the log readable.
        </li>
      </ul>

      <h2>Security model</h2>
      <p>
        We never store the script source in the audit log — only a SHA-256 hash. Secrets read via{' '}
        <code>rynora.vault.read(key)</code> are encrypted at rest and decrypted only inside the script process; the
        plaintext never leaves the runner's memory. If you suspect a leak, rotate the secret from{' '}
        <strong>Project → Variables</strong> and search the audit log for the access timestamp.
      </p>
    </DocsLayout>
  );
}
